import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getRooms,
  createRoom,
  updateRoom,
  deleteRoom,
} from "../../../services/RoomService";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const emptyForm = {
  code: "",
  name: "",
  building: "",
  floor: "",
  capacity_normal: "",
  capacity_exam: "",
  has_wifi_zone: false,
  is_pmr_accessible: false,
  is_active: true,
};

const Rooms = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.ROOM_CREATE);
  const canUpdate = can(PERMISSIONS.ROOM_UPDATE);
  const canDelete = can(PERMISSIONS.ROOM_DELETE);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "name", sort_dir: "asc" };
      if (search) params.search = search;
      if (isActiveFilter !== "") params.is_active = isActiveFilter;

      const res = await getRooms(params);
      const body = res.data;
      setRooms(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.rooms.impossibleChargerListeSalles"));
      setRooms([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, isActiveFilter, t]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setIsActiveFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (room) => {
    setFormMode("edit");
    setFormData({
      code: room.code || "",
      name: room.name || "",
      building: room.building || "",
      floor: room.floor || "",
      capacity_normal: room.capacity_normal ?? "",
      capacity_exam: room.capacity_exam ?? "",
      has_wifi_zone: !!room.has_wifi_zone,
      is_pmr_accessible: !!room.is_pmr_accessible,
      is_active: !!room.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(room.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.code.trim()) errors.code = t("common.ui.leCodeEstRequis");
    if (!formData.name.trim()) errors.name = t("common.ui.leNomEstRequis");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        building: formData.building.trim() || null,
        floor: formData.floor.trim() || null,
        capacity_normal: formData.capacity_normal === "" ? null : Number(formData.capacity_normal),
        capacity_exam: formData.capacity_exam === "" ? null : Number(formData.capacity_exam),
        has_wifi_zone: formData.has_wifi_zone,
        is_pmr_accessible: formData.is_pmr_accessible,
        is_active: formData.is_active,
      };

      if (formMode === "add") {
        await createRoom(payload);
        setFeedback({ type: "success", message: t("pages.rooms.salleCreeeAvecSucces") });
      } else {
        await updateRoom(editingId, payload);
        setFeedback({ type: "success", message: t("pages.rooms.salleMiseJour") });
      }

      setShowFormModal(false);
      fetchRooms();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRoom(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.rooms.salleSupprimee") });
      setDeleteTarget(null);
      if (rooms.length === 1 && page > 1) setPage((prev) => prev - 1);
      else fetchRooms();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.rooms.impossibleSupprimerSalle") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="/dashboard">{t("menu.dashboard.title")}</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{t("pages.rooms.sallesExamen")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" title={t("common.crud.refresh")} onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
<button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.rooms.ajouterSalle")}
              </button>
)}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.rooms.listeSalles")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.rooms.rechercherCodeNomBatiment")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={isActiveFilter}
                  onChange={(e) => { setIsActiveFilter(e.target.value); setPage(1); }}
                >
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="true">{t("common.ui.actives")}</option>
                  <option value="false">{t("common.ui.inactives")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.fields.code")}</th>
                    <th>{t("common.fields.name")}</th>
                    <th>{t("pages.rooms.batimentEtage")}</th>
                    <th>{t("pages.rooms.capaciteNormaleExamen")}</th>
                    <th>Wifi</th>
                    <th>{t("pages.rooms.pmr")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="8" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("common.crud.loading")}</span></div>
                    </td></tr>
                  ) : loadError ? (
                    <tr><td colSpan="8" className="text-center py-4 text-danger">{loadError}</td></tr>
                  ) : rooms.length > 0 ? (
                    rooms.map((room) => (
                      <tr key={room.id}>
                        <td><span className="fw-medium">{room.code}</span></td>
                        <td>{room.name}</td>
                        <td>{[room.building, room.floor].filter(Boolean).join(" / ") || "-"}</td>
                        <td>{room.capacity_normal ?? "-"} / {room.capacity_exam ?? "-"}</td>
                        <td>{room.has_wifi_zone ? <i className="ti ti-wifi text-success"></i> : <i className="ti ti-wifi-off text-muted"></i>}</td>
                        <td>{room.is_pmr_accessible ? <i className="ti ti-wheelchair text-success"></i> : "-"}</td>
                        <td>
                          <span className={`badge badge-soft-${room.is_active ? "success" : "danger"} d-inline-flex align-items-center`}>
                            <i className="ti ti-circle-filled fs-5 me-1"></i>
                            {room.is_active ? t("common.ui.active") : t("common.ui.inactive")}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle as="button" className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false">
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(room)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(room)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="8" className="text-center py-4">
                      <i className="ti ti-search-off fs-1 text-muted"></i>
                      <h5 className="mt-2">{t("pages.rooms.aucuneSalleTrouvee")}</h5>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-3 pt-3 border-top">
                <span className="text-sm text-gray-600">{t("pages.rooms.salle", { total })}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{t("common.ui.precedent")}</button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>{t("common.ui.suivant")}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.rooms.ajouterSalle") : t("pages.rooms.modifierSalle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label">{t("common.fields.code")} <span className="text-danger">*</span></label>
                <input type="text" className={`form-control ${formErrors.code ? "is-invalid" : ""}`} value={formData.code}
                  onChange={(e) => handleFormChange("code", e.target.value)} />
                {formErrors.code && <div className="invalid-feedback">{formErrors.code}</div>}
              </div>
              <div className="col-6 mb-3">
                <label className="form-label">{t("common.fields.name")} <span className="text-danger">*</span></label>
                <input type="text" className={`form-control ${formErrors.name ? "is-invalid" : ""}`} value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)} />
                {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
              </div>
            </div>
            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label">{t("pages.rooms.batiment")}</label>
                <input type="text" className="form-control" value={formData.building}
                  onChange={(e) => handleFormChange("building", e.target.value)} />
              </div>
              <div className="col-6 mb-3">
                <label className="form-label">{t("pages.rooms.etage")}</label>
                <input type="text" className="form-control" value={formData.floor}
                  onChange={(e) => handleFormChange("floor", e.target.value)} />
              </div>
            </div>
            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label">{t("pages.rooms.capaciteNormale")}</label>
                <input type="number" min="0" className="form-control" value={formData.capacity_normal}
                  onChange={(e) => handleFormChange("capacity_normal", e.target.value)} />
              </div>
              <div className="col-6 mb-3">
                <label className="form-label">{t("pages.rooms.capaciteExamen")}</label>
                <input type="number" min="0" className="form-control" value={formData.capacity_exam}
                  onChange={(e) => handleFormChange("capacity_exam", e.target.value)} />
              </div>
            </div>
            <div className="form-check form-switch mb-2">
              <input className="form-check-input" type="checkbox" role="switch" id="room-wifi"
                checked={formData.has_wifi_zone} onChange={(e) => handleFormChange("has_wifi_zone", e.target.checked)} />
              <label className="form-check-label" htmlFor="room-wifi">{t("pages.rooms.zoneWiFiDisponible")}</label>
            </div>
            <div className="form-check form-switch mb-2">
              <input className="form-check-input" type="checkbox" role="switch" id="room-pmr"
                checked={formData.is_pmr_accessible} onChange={(e) => handleFormChange("is_pmr_accessible", e.target.checked)} />
              <label className="form-check-label" htmlFor="room-pmr">{t("pages.rooms.accessiblePmr")}</label>
            </div>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id="room-active"
                checked={formData.is_active} onChange={(e) => handleFormChange("is_active", e.target.checked)} />
              <label className="form-check-label" htmlFor="room-active">{t("pages.rooms.salleActive")}</label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>{t("common.crud.cancel")}</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon"><i className="ti ti-trash-x"></i></span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p><Trans i18nKey="pages.rooms.voulezVousVraimentSupprimerSalle" values={{ name: deleteTarget?.name }} components={[<strong />]} /></p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>{t("common.crud.cancel")}</button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Rooms;
